import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Box, Divider, Stack, Typography } from '@mui/joy'
import { ClientLoadModel } from '@numeo/types-core'
import { VerticalDirectionIcon } from 'assets/icons/DatDirectionIcons'
import { useMemo } from 'react'
import { formatFieldName, formatFieldValue } from 'utils/formatters'
import React from 'react'
import { DTMFKeypadRef } from 'layouts/shared-widgets/components/call-widgets/components/call-widget/components/widget-subcomponents/DTMFKeypad'

/**
 * TripColumn component displays trip information when the load row is expanded.
 * This includes:
 * - Pickup and delivery details
 * - Contact information
 * - Equipment requirements
 * - Additional comments
 */

export const TripColumn: React.FC<TripColumnProps> = ({ load }) => {
    const renderAvailabilityDates = () => {
        return new Date(load.earliestAvailability).toDateString().split(' ').slice(1, 3).join(' ') ===
            new Date(load.latestAvailability).toDateString().split(' ').slice(1, 3).join(' ')
            ? new Date(load.earliestAvailability).toDateString().split(' ').slice(1, 3).join(' ')
            : `${new Date(load.earliestAvailability).toDateString().split(' ').slice(1, 3).join(' ')} - ${new Date(load.latestAvailability).toDateString().split(' ').slice(1, 3).join(' ')}`
    }

    const renderTripDetails = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                {/* Icon Section */}
                <VerticalDirectionIcon />

                {/* Origin and Destination Details */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    {/* Origin */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            level="body-md"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                            }}
                        >
                            {`${load.origin.city}, ${load.origin.stateProv} ${load.originDeadheadMiles?.miles ? '(' + load.originDeadheadMiles?.miles + ')' : ''}`}
                        </Typography>
                        <Typography>{renderAvailabilityDates()}</Typography>
                    </Box>

                    {/* Destination */}
                    <Typography
                        level="body-md"
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                        }}
                    >
                        {`${load.destination.city}, ${load.destination.stateProv}`}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const renderEquipmentDetails = () => {
        return (
            <Box>
                <Typography level="body-md" fontWeight="bold">
                    Equipment
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Truck */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: '200px' }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                minWidth: '60px',
                            }}
                        >
                            Truck
                        </Typography>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                                textAlign: 'right',
                            }}
                        >
                            {load.equipmentType && load.equipmentType.toUpperCase() !== 'UNKNOWN' ? load.equipmentType : '—'}
                        </Typography>
                    </Box>
                    {/* Load */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Load
                        </Typography>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                            }}
                        >
                            {load.shipmentDetails?.fullPartial || '—'}
                        </Typography>
                    </Box>
                    {/* Length */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Length
                        </Typography>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                            }}
                        >
                            {load.shipmentDetails?.maximumLengthFeet ? `${load.shipmentDetails?.maximumLengthFeet} ft` : '—'}
                        </Typography>
                    </Box>
                    {/* Weight */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Weight
                        </Typography>
                        <Typography
                            level="body-sm"
                            sx={{
                                fontSize: '0.8rem',
                            }}
                        >
                            {load.shipmentDetails?.maximumWeightPounds ? `${load.shipmentDetails?.maximumWeightPounds.toLocaleString()} lbs` : '—'}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    const renderAiGatheredNotes = () => {
        return (
            <Stack direction={'column'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon fontSize="small" />
                    <Typography level="body-md" fontWeight="bold">
                        AI Gathered Notes
                    </Typography>
                </Box>
                <Typography level="body-sm">{load.aiGatheredNotes?.fromCall || ''}</Typography>
                <Typography level="body-sm">{load.aiGatheredNotes?.fromEmail || ''}</Typography>
            </Stack>
        )
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
            }}
        >
            {/* Header */}
            <Typography
                sx={{
                    color: 'text.secondary',
                    backgroundColor: 'background.surface',
                    px: 1,
                    fontWeight: 'bold',
                }}
            >
                Trip
            </Typography>
            <Stack direction={{ sm: 'column', md: 'row' }} justifyContent="space-between" px={1} alignItems="center" width="100%">
                {renderTripDetails()}
                {renderEquipmentDetails()}
            </Stack>
            <Stack spacing={1}>
                <CommentDetails load={load} />
                {load.aiGatheredNotes?.fromCall || load.aiGatheredNotes?.fromEmail ? renderAiGatheredNotes() : null}
            </Stack>
        </Box>
    )
}
/**
 * Props for the TripColumn component that shows expanded load information
 */
export interface TripColumnProps {
    load: ClientLoadModel
    loadSearchId: string
    dtmfKeypadRef?: React.RefObject<DTMFKeypadRef>
}

const CommentDetails = ({ load }: { load: ClientLoadModel }) => {
    const updatedFields = useMemo(() => {
        const updates: Record<string, unknown> = {}

        const emailDetails = load.emailHistory?.details as Record<string, unknown> | undefined
        for (const key in emailDetails || {}) {
            if (
                !['_id', 'id', '__v', 'suggestedEmails', 'isInfoRequestFinished'].includes(key) &&
                emailDetails?.[key] &&
                (Array.isArray(emailDetails[key]) ? (emailDetails[key] as unknown[]).length : true)
            ) {
                updates[key] = emailDetails[key]
            }
        }

        for (const call of load.callHistory || []) {
            const callDetails = call.details as Record<string, unknown> | undefined
            for (const key in callDetails || {}) {
                if (
                    !['_id', 'id', '__v', 'suggestedEmails', 'isInfoRequestFinished'].includes(key) &&
                    callDetails?.[key] &&
                    (Array.isArray(callDetails?.[key]) ? (callDetails?.[key] as unknown[]).length : true)
                ) {
                    updates[key] = callDetails?.[key]
                }
            }
        }

        return Object.entries(updates)
    }, [load.callHistory, load.emailHistory?.details])

    if (!updatedFields.length && !load.comments) {
        return null
    }

    return (
        <Stack direction={'column'}>
            {load.comments && (
                <>
                    <Typography level="body-md" fontWeight="bold">
                        Comments
                    </Typography>
                    <Typography level="body-sm">{load.comments || '—'}</Typography>
                </>
            )}
            {updatedFields.length > 0 && (
                <>
                    <Divider sx={{ my: 1 }} />
                    {updatedFields.map(([key, value]) => (
                        <Box key={key} sx={{ display: 'flex', gap: 0.5 }}>
                            <Typography level="body-sm">
                                <b> {formatFieldName(key)}:</b> {formatFieldValue(value, key)}
                            </Typography>
                        </Box>
                    ))}
                </>
            )}
        </Stack>
    )
}
